import { ChevronRight } from "lucide-react";
import { Link } from "react-router";
import { cn } from "@repo/utils";

export type BreadcrumbItem = {
  label: string;
  /**
   * Cible du lien. Absent = segment non cliquable : soit la page courante
   * (dernier segment), soit un niveau qui n'a pas d'URL propre.
   */
  href?: string;
};

/**
 * Fil d'Ariane du portail admin, où la navigation est hiérarchique
 * (organisation → campagne → parcours) et où la navbar ne porte plus qu'un seul
 * lien : c'est lui qui rend la position courante lisible et permet de remonter.
 *
 * Le dernier segment n'est jamais cliquable — c'est la page affichée. Les labels
 * sont tronqués individuellement plutôt que de laisser la ligne pousser la page
 * en défilement horizontal : un nom de campagne peut être très long.
 */
export function Breadcrumb({
  items,
  className,
}: {
  items: BreadcrumbItem[];
  className?: string;
}) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Fil d'Ariane" className={cn("mb-4", className)}>
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
        {items.map((item, index) => {
          const dernier = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-x-1.5 min-w-0">
              {index > 0 && (
                <ChevronRight
                  size={14}
                  aria-hidden="true"
                  className="shrink-0 opacity-50"
                />
              )}
              {dernier ? (
                <span
                  aria-current="page"
                  className="truncate max-w-[16rem] font-medium text-foreground"
                >
                  {item.label}
                </span>
              ) : item.href ? (
                <Link
                  to={item.href}
                  className="truncate max-w-[12rem] rounded-full transition-colors hover:text-foreground hover:underline"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="truncate max-w-[12rem]">{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
